import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Row, Col, Spinner, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ControlledMenu, MenuItem, SubMenu } from "@szhsin/react-menu";
import { Button, Form } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Rect } from "react-konva";
import { matchApi } from "../../api";
import Preloader from "@/components/Elements/Preloader";
import ProgressBar from "./ProgressBar";
import VideoPlayer from "@/components/VideoPlayer";
import { SuperTrackMarkupSuperTrack } from "@/pages/SuperTracks/SuperTrackMarkupSuperTrack";
import { getFormattedDuration } from "@/util/video-player";
import {
  generateSuperTrackMapV2,
  countLargeGaps,
  isJsonString,
  cn,
  isTimeInRange,
} from "@/util/functions";
import { LABELER, DASHBOARD } from "@/constants/routs.constants";

import "./SuperTrackMarkup.scss";
import { useSuperTracks } from "@/hooks/useSuperTracks";
import useTask from "@/hooks/useTask";
import "@szhsin/react-menu/dist/index.css";
import { CUT_MODES } from "@/constants";
import { useActions } from "@/hooks/useActions";
import { useChunkedTrackBoxes } from "@/hooks/useChunkedTrackBoxes";
import {
  addSuperTrack,
  appendFrameBoxes,
  cutSuperTrack,
  deleteSuperTrack,
  deleteTrack,
  mergeSuperTrack,
  moveTracks,
  setBoxes,
  setChangedKeyFrames,
  setFrameBoxes,
  setFrameCount,
  setNewBoxes,
  setSuperTrackEndTime,
  setSuperTrackGlobalVideoDuration,
  setSuperTracks,
  setSuperTrackStartTime,
  mergeTrackBoxesChunk,
  updateError,
  updateSuperTrack,
} from "@/store/supertrackSlice";
import { isEqual } from "lodash";
import Switch from "@/components/Elements/switch";
import MyButton from "@/components/Elements/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HourGlassButton from "@/components/Elements/HourGlassButton";
import { resetSaveFunction } from "@/store/communicationSlice";
import * as Sentry from "@sentry/react";

export default function SuperTrackMarkup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { videoId, trackId } = useParams();

  const dispatch = useDispatch();

  // #region custom hooks
  const { hasIntersection } = useSuperTracks();
  const { taskId, taskType, frameStart, frameEnd } = useTask();
  const { merge, cut, replace } = useActions();
  const { loadedRange, checkPrefetch, handleSeek } = useChunkedTrackBoxes(videoId);
  // #endregion custom hooks

  const {
    superTracks,
    trackBoxes,
    isCutActive,
    cutFrame,
    boxes,
    newBoxes,
    changedKeyFrames,
  } = useSelector(({ superTracks }) => superTracks);

  const { user } = useSelector(({ auth }) => auth);

  const triggerSave = useSelector((state) => state.communication.triggerSave);

  // #region Refs definition
  const videoRef = useRef();
  const newRectRef = useRef();
  const newRect = useRef({
    visible: false,
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 0,
  });
  const percentRef = useRef();
  const posRef = useRef();
  const trackBarRef = useRef();
  const menuRef = useRef();
  const isCutting = useRef(false);
  const realtimeSync = useRef(true);
  const storingRef = useRef(false);
  const newBoxesRef = useRef({});
  const changedKeyFramesRef = useRef([]);
  const consistencyCooldown = useRef(false);
  const prevCurrentStRef = useRef(null);
  const prevNewBoxesRef = useRef(null);
  const prevTrackBoxesRef = useRef(null);
  const prevProgressStRef = useRef(null);
  const prevProgressBoxesRef = useRef(null);
  const activeTracksRef = useRef({ set: new Map(), nextIdx: 0, lastFrame: -1 });
  const candidateTracksRef = useRef({ set: new Map(), nextIdx: 0, lastFrame: -1 });
  const mainTrackRef = useRef({ idx: 0, lastFrame: -1 });
  const lastSearchGapRef = useRef(null); // tracks which gap we last searched: { tracks, idx }
  const mergeSearchIdxRef = useRef(0);
  // #endregion Refs definition
  // #region States definition
  const [videoPath, setVideoPath] = useState("");
  const [currentFrame, setCurrentFrame] = useState(0);
  const [frameTime, setFrameTime] = useState(0);
  const [videoDomWidth, setVideoDomWidth] = useState(0);
  const [videoDomHeight, setVideoDomHeight] = useState(0);
  const [videoWidth, setVideoWidth] = useState(0);
  const [videoHeight, setVideoHeight] = useState(0);
  const [ratio, setRatio] = useState(1);
  const [step, setStep] = useState(30);
  const [videoDuration, setVideoDuration] = useState(0);
  const [stageScale, setStageScale] = useState(1);
  const [stageX, setStageX] = useState(0);
  const [stageY, setStageY] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [totalFrames, setTotalFrames] = useState(0);
  const [interpol, setInterpol] = useState(true);
  const [interpolStart, setInterpolStart] = useState(null);
  const [interpolEnd, setInterpolEnd] = useState(null);
  const [overwriteMode, setOverwriteMode] = useState(false);
  const [fps, setFps] = useState(0);
  const [lastUpdatedFrame, setLastUpdatedFrame] = useState(null);
  const [changedFrameCnt, setChangedFrameCnt] = useState(0);
  const [changedFrames, setChangedFrames] = useState([]);
  const [candidates, setCandidates] = useState({});
  const [frameStep, setFrameStep] = useState(1);
  const [menuVisible, setMenuVisible] = useState(false);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [selectedSuperTrackId, setSelectedSuperTrackId] = useState(null);
  const [currentSuperTrack, setCurrentSuperTrack] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(); // This is used for cutting
  const [imageY, setImageY] = useState(0);
  const [isStoring, setIsStoring] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [gapCounts, setGapCounts] = useState(0);
  const [trackIds, setTrackIds] = useState({});
  const [colors, setColors] = useState({});
  const [autoZoom, setAutoZoom] = useState(false);
  const [realTimeSync, setRealTimeSync] = useState(true);
  const [stopFrame, setStopFrame] = useState(null);
  const [pbRate, setPbRate] = useState(null);
  const [tempRectActive, setTempRectActive] = useState(false); // Indicate whether the rect is activated or not
  const [progressTracks, setProgressTracks] = useState([]);
  const [gamePausedOut, setGamePausedOut] = useState(null);
  const [gamePausedInactive, setGamePausedInactive] = useState(null);
  const [pauseList, setPauseList] = useState([]);
  const [allowed, setAllowed] = useState(true);
  const [superTrackType, setSuperTrackType] = useState(null);
  const [allowPauseEditing, setAllowPauseEditing] = useState(false);
  const [jersey, setJersey] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [rightClickedTrackInfo, setRightClickedTrackInfo] = useState(null);
  const [autoNext, setAutoNext] = useState(taskType === "ball_supertrack");
  const [consistencyStatus, setConsistencyStatus] = useState(null);
  const [consistencyDiffs, setConsistencyDiffs] = useState([]);
  const [showConsistencyDiffs, setShowConsistencyDiffs] = useState(false);
  const [showTrackBar, setShowTrackBar] = useState(false);
  const [taskStatuses, setTaskStatuses] = useState({});
  // #endregion States definition

  // Periodically fetch task statuses for all supertracks in this match
  useEffect(() => {
    if (!videoId) return;
    let cancelled = false;
    const fetch = async () => {
      try {
        const res = await matchApi.getTaskStatuses(videoId);
        if (!cancelled && res) setTaskStatuses(res);
      } catch (e) {
        console.warn('[taskStatuses] fetch error:', e);
      }
    };
    fetch();
    const interval = setInterval(fetch, 120_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [videoId]);

  const memoizedBoxes = useMemo(
    () => ({ main: boxes, ...candidates }),
    [boxes, candidates]
  );

  // Sorted tracks of main supertrack for O(1) unmarked check
  const sortedMainTracks = useMemo(() => {
    if (!superTracks || !superTracks[trackId]?.tracks?.length) return null;
    return [...superTracks[trackId].tracks]
      .filter((t) => t.frame_start != null && t.frame_end != null)
      .sort((a, b) => a.frame_start - b.frame_start);
  }, [superTracks, trackId]);

  // Sorted tracks for sweep-line (used by both showAll and merge candidates)
  // For ball tasks, no candidates needed — each labeler works on isolated time range
  const sortedAllTracks = useMemo(() => {
    if (taskType === "ball_supertrack") return null;
    if (!superTracks || !trackBoxes) return null;
    const tracks = [];
    for (const stId in superTracks) {
      if (String(stId) === String(trackId)) continue;
      const st = superTracks[stId];
      if (!st?.tracks?.length) continue;
      if (st.kind === "ball") continue;
      for (const t of st.tracks) {
        if (t.track_id == null || !trackBoxes[t.track_id]) continue;
        tracks.push({
          track_id: t.track_id,
          stId,
          frame_start: t.frame_start,
          frame_end: t.frame_end,
        });
      }
    }
    tracks.sort((a, b) => a.frame_start - b.frame_start);
    return tracks;
  }, [superTracks, trackBoxes, trackId, taskType]);

  useEffect(() => {
    if (triggerSave && taskType !== "ball_supertrack") {
      saveAnswer(triggerSave);
      dispatch(resetSaveFunction());
    }
  }, [triggerSave]);

  // Websocket handler for data synchronization across labelers
  // Skip WS for ball markup — each labeler works on their own time range
  useEffect(() => {
    if (taskType === "ball_supertrack") return;

    let socket = null;

    const createWebSocketConnection = () => {
      socket = new WebSocket(
        `${process.env.REACT_APP_PANEL_WS}match/${videoId}/super-tracks/`
      );

      socket.onopen = () => {
        console.log("WebSocket connection established.");
        const token = localStorage.getItem("access");
        socket.send(token);
        toast.success("WebSocket connection established", {
          position: "top-center",
          autoClose: 2000,
        });
      };

      socket.onmessage = (event) => {
        if (realtimeSync.current) {
          if (isJsonString(event.data)) {
            const parsedData = JSON.parse(event.data);
            console.log(`[WS] ${parsedData.operation}`, parsedData);
            switch (parsedData.operation) {
              case "create": {
                const { super_track } = parsedData;
                dispatch(addSuperTrack(super_track));
                consistencyCooldown.current = true;
                setTimeout(() => { consistencyCooldown.current = false; }, 10_000);
                break;
              }
              case "merge": {
                const { new_super_track_uid, old_super_track_uid } = parsedData;
                dispatch(
                  mergeSuperTrack({
                    targetId: new_super_track_uid,
                    superTrackId: old_super_track_uid,
                  })
                );
                consistencyCooldown.current = true;
                setTimeout(() => { consistencyCooldown.current = false; }, 10_000);
                break;
              }
              case "delete": {
                const { super_track_uid } = parsedData;
                dispatch(deleteSuperTrack(super_track_uid));
                consistencyCooldown.current = true;
                setTimeout(() => { consistencyCooldown.current = false; }, 10_000);
                break;
              }
              case "delete_track": {
                const { track_uid, super_track_uid } = parsedData;
                dispatch(
                  deleteTrack({
                    track_id: track_uid,
                    superTrackId: super_track_uid,
                  })
                );
                consistencyCooldown.current = true;
                setTimeout(() => { consistencyCooldown.current = false; }, 10_000);
                break;
              }
              case "cut": {
                const {
                  super_track_uid,
                  response_data: { supertrack, tracks },
                } = parsedData;
                dispatch(
                  updateSuperTrack({
                    superTrackId: super_track_uid,
                    tracks: supertrack,
                  })
                );
                dispatch(appendFrameBoxes({ newBoxes: tracks }));
                consistencyCooldown.current = true;
                setTimeout(() => { consistencyCooldown.current = false; }, 10_000);
                break;
              }
              case "replace": {
                const { new_super_track_uid, super_track_uid, tracks_ids } =
                  parsedData;
                dispatch(
                  moveTracks({
                    fromId: super_track_uid,
                    toId: new_super_track_uid,
                    trackIds: tracks_ids,
                  })
                );
                consistencyCooldown.current = true;
                setTimeout(() => { consistencyCooldown.current = false; }, 10_000);
                break;
              }
              case "update": {
                const { super_track_uid, supertrack, tracks } = parsedData;
                dispatch(
                  updateSuperTrack({
                    superTrackId: super_track_uid,
                    tracks: supertrack,
                  })
                );
                dispatch(appendFrameBoxes({ newBoxes: tracks }));
                consistencyCooldown.current = true;
                setTimeout(() => { consistencyCooldown.current = false; }, 10_000);
                break;
              }
              default: {
                break;
              }
            }
          }
        }
      };

      socket.onclose = () => {
        console.log("WebSocket connection closed. Reconnecting...");
        setTimeout(() => {
          createWebSocketConnection();
        }, 5000);
      };
    };

    createWebSocketConnection();

    return () => {
      if (socket) {
        socket.onclose = function () { };
        socket.close();
        socket = null;
      }
    };
  }, []);

  // Consistency check: poll backend every 60s
  const superTracksRef = useRef(superTracks);
  superTracksRef.current = superTracks;

  useEffect(() => {
    if (!videoId || !dataLoaded || taskType === "ball_supertrack") return;

    const check = async () => {
      const st = superTracksRef.current;
      if (!st || !st[trackId] || consistencyCooldown.current) return;
      try {
        const tracks = (st[trackId].tracks || []).map(t => ({
          track_id: t.track_id,
          frame_start: t.frame_start,
          frame_end: t.frame_end,
        }));
        const data = await matchApi.checkConsistency(videoId, trackId, tracks);
        setConsistencyStatus(data.consistent ? 'green' : 'red');
        setConsistencyDiffs(data.diff ? [data.diff] : []);
        if (!data.consistent) {
          console.warn('[ConsistencyCheck] DIFF for supertrack', trackId, ':', JSON.stringify(data.diff, null, 2));
        }
      } catch (err) {
        console.error('[ConsistencyCheck] error:', err);
      }
    };

    const intervalId = setInterval(check, 60_000);
    check();
    return () => clearInterval(intervalId);
  }, [videoId, dataLoaded]);

  // To get combined boxes from orignal db boxes and newly boxes
  // Combined boxes are used to prevent boxes from being cleared when supertrack data changes(like when merging or cutting)
  useEffect(() => {
    if (!superTracks || !trackBoxes) return;

    const currentSt = superTracks[trackId];
    if (!currentSt?.tracks || !Array.isArray(currentSt.tracks)) return;

    // Skip if current ST, newBoxes, and trackBoxes all unchanged (external event on another ST)
    const stChanged = currentSt !== prevCurrentStRef.current;
    const nbChanged = newBoxes !== prevNewBoxesRef.current;
    const tbChanged = trackBoxes !== prevTrackBoxesRef.current;
    prevCurrentStRef.current = currentSt;
    prevNewBoxesRef.current = newBoxes;
    prevTrackBoxesRef.current = trackBoxes;
    if (!stChanged && !nbChanged && !tbChanged) return;

    let originalBoxes = {};
    for (const track of currentSt.tracks) {
      const { track_id } = track;
      Object.assign(originalBoxes, trackBoxes[track_id]);
    }
    Object.assign(originalBoxes, newBoxes);
    if (isEqual(boxes, originalBoxes)) {
      return; // means there's no change
    }
    dispatch(setBoxes(originalBoxes));
  }, [superTracks, trackBoxes, newBoxes]);

  useEffect(() => {
    setGapCounts(countLargeGaps(Object.keys(boxes), step, totalFrames));
  }, [boxes, step, totalFrames]);

  useEffect(() => {
    if (!superTracks || !trackBoxes) return;

    const currentSt = superTracks[trackId];
    if (!currentSt?.tracks || !Array.isArray(currentSt.tracks)) return;

    // Immer preserves reference for untouched objects — fast skip
    if (currentSt === currentSuperTrack) return;

    setCurrentSuperTrack(currentSt);
  }, [superTracks, trackBoxes]);

  useEffect(() => {
    if (!videoId || !trackId) return;

    async function fetchMatchInfo() {
      try {
        const match = await matchApi.getMatch(videoId);
        if (!match) return;

        const { fps, origin } = match.video;
        setFps(fps);
        setFrameTime(1 / fps);
        setVideoPath(origin);

        const { pipeline_passing } = match;
        const isBroadcastingPipeline =
          pipeline_passing === "broadcasting" ||
          pipeline_passing === "broadcasting_draw";

        // fetch superTracks from db — chunked loading (first 5 minutes)
        const initialFrameEnd = Math.round(fps * 300);
        const res = await matchApi.getMatchSuperTracksFull(videoId, {
          frame_start: 0,
          frame_end: initialFrameEnd,
        });
        const { frame_step, supertracks, tracks, pauses } = res;
        if (!supertracks || !tracks) return;

        // Set SuperTrack Type
        setSuperTrackType(supertracks[trackId]?.kind);
        if (supertracks[trackId]?.kind === "ball") {
          setStep(isBroadcastingPipeline ? 30 : 20);
        }

        if (pauses && pauses.length > 0) {
          setPauseList(
            pauses.map(({ start, end, type }) => [start, end, type])
          );
        }
        // saved all coords with trackIds. this data is used for finding candidates
        dispatch(setFrameBoxes(tracks));
        loadedRange.current = [[0, initialFrameEnd]];
        if (frame_step) setFrameStep(frame_step);

        dispatch(setSuperTracks(generateSuperTrackMapV2(supertracks)));
        setDataLoaded(true);
      } catch (error) {
        Sentry.captureException(error); // Report to Sentry
        console.log(error);
        dispatch(updateError(error));
      }
    }

    fetchMatchInfo();
  }, [videoId, trackId]);

  useEffect(() => {
    if (superTrackType === "ball" && user?.is_staff) {
      setAllowPauseEditing(true);
    }
  }, [superTrackType, user]);

  // O(1) per-frame: is main supertrack unmarked at currentFrame?
  const isMainUnmarked = useMemo(() => {
    if (!sortedMainTracks || sortedMainTracks.length === 0) return true;
    const mRef = mainTrackRef.current;
    const frame = currentFrame;

    // Reset pointer when tracks array changed (merge/cut/load)
    if (mRef.tracks !== sortedMainTracks) {
      mRef.idx = 0;
      mRef.lastFrame = -1;
      mRef.tracks = sortedMainTracks;
    }

    // Reset pointer on backward seek
    if (frame < mRef.lastFrame) mRef.idx = 0;
    mRef.lastFrame = frame;

    // Advance past tracks that ended before current frame
    while (mRef.idx < sortedMainTracks.length &&
           sortedMainTracks[mRef.idx].frame_end < frame) {
      mRef.idx++;
    }

    // If pointer is past all tracks, unmarked
    if (mRef.idx >= sortedMainTracks.length) {
      return true;
    }

    // Current track covers this frame?
    const unmarked = sortedMainTracks[mRef.idx].frame_start > frame;
    return unmarked;
  }, [currentFrame, sortedMainTracks]);

  // ShowAll mode: sweep-line for active tracks
  useEffect(() => {
    if (!showAll) return;
    if (!sortedAllTracks || !trackBoxes) {
      if (Object.keys(candidates).length > 0) {
        setCandidates({});
        setTrackIds({});
      }
      return;
    }

    const ref = activeTracksRef.current;
    const frame = currentFrame;

    if (ref.tracks !== sortedAllTracks) {
      ref.set = new Map(); ref.nextIdx = 0; ref.lastFrame = -1; ref.tracks = sortedAllTracks;
    }

    if (frame < ref.lastFrame) {
      ref.set = new Map(); ref.nextIdx = 0;
      while (ref.nextIdx < sortedAllTracks.length &&
             sortedAllTracks[ref.nextIdx].frame_start <= frame) {
        const t = sortedAllTracks[ref.nextIdx];
        if (t.frame_end >= frame) ref.set.set(t.track_id, t);
        ref.nextIdx++;
      }
    } else {
      for (const [tid, t] of ref.set) {
        if (t.frame_end < frame) ref.set.delete(tid);
      }
      while (ref.nextIdx < sortedAllTracks.length &&
             sortedAllTracks[ref.nextIdx].frame_start <= frame) {
        const t = sortedAllTracks[ref.nextIdx];
        if (t.frame_end >= frame) ref.set.set(t.track_id, t);
        ref.nextIdx++;
      }
    }
    ref.lastFrame = frame;

    // Diff: only update state if set of track ids changed
    const prevKeys = Object.keys(candidates);
    let changed = prevKeys.length !== ref.set.size;
    if (!changed) {
      for (const [tid] of ref.set) {
        if (!(tid in candidates)) { changed = true; break; }
      }
    }
    if (!changed) return;

    const newCandidates = {};
    const newIds = {};
    const newColors = {};
    for (const [tid, t] of ref.set) {
      newCandidates[tid] = trackBoxes[tid];
      newIds[tid] = `${t.stId}:${tid}`;
      const status = taskStatuses[t.stId]?.status;
      newColors[tid] = status === "finished" ? "green"
        : status === "in progress" ? "orange"
        : "blue";
    }
    setCandidates(newCandidates);
    setTrackIds(newIds);
    setColors(newColors);
  }, [currentFrame, sortedAllTracks, trackBoxes, showAll, taskStatuses]);

  // Progressive search boundary — expands by 15s each time user scrolls past it.
  // Only triggers re-render when boundary actually moves (every ~900 frames).
  const [candidateSearchEnd, setCandidateSearchEnd] = useState(0);

  useEffect(() => {
    if (!fps || !superTracks?.[trackId]?.tracks?.length) return;
    const storedEnd = superTracks[trackId].tracks.reduce(
      (max, t) => Math.max(max, t.frame_end ?? 0), 0
    );
    const windowFrames = Math.round(15 * fps);
    const steps = Math.max(1, Math.floor(Math.max(0, currentFrame - storedEnd) / windowFrames) + 1);
    const needed = storedEnd + steps * windowFrames;
    setCandidateSearchEnd(prev => {
      if (needed <= prev) return prev;
      console.log(`[mergeCandidates] expanding search: ${prev} → ${needed} (storedEnd=${storedEnd} frame=${currentFrame})`);
      return needed;
    });
  }, [currentFrame, fps, superTracks, trackId]);

  // Merge candidates: supertracks of the same kind starting between
  // storedEnd and candidateSearchEnd. Recalculates only when search
  // boundary expands or supertracks change (merge/store).
  const mergeCandidateSts = useMemo(() => {
    if (taskType === "ball_supertrack") return null;
    if (!superTracks || !superTracks[trackId]?.tracks?.length || !candidateSearchEnd) return null;

    const currentSt = superTracks[trackId];
    const currentKind = currentSt.kind;
    const storedEnd = currentSt.tracks.reduce(
      (max, t) => Math.max(max, t.frame_end ?? 0), 0
    );
    if (storedEnd <= 0) return null;

    const result = [];
    let skippedKind = 0;

    for (const stId in superTracks) {
      if (String(stId) === String(trackId)) continue;
      const st = superTracks[stId];
      if (!st?.tracks?.length) continue;
      if (currentKind && st.kind && st.kind !== currentKind) { skippedKind++; continue; }

      const stStart = st.tracks.reduce(
        (min, t) => Math.min(min, t.frame_start ?? Infinity), Infinity
      );
      if (stStart >= storedEnd && stStart <= candidateSearchEnd) {
        result.push({ stId, distance: stStart - storedEnd });
      }
    }

    console.log(`[mergeCandidates] kind=${currentKind} storedEnd=${storedEnd} searchEnd=${candidateSearchEnd} found=${result.length} skippedKind=${skippedKind}`,
      result.slice(0, 10).map(r => `st#${r.stId} +${r.distance}frames`));
    result.sort((a, b) => a.distance - b.distance);
    return result.length > 0 ? result : null;
  }, [superTracks, trackId, taskType, candidateSearchEnd]);

  // Pre-sorted tracks from candidate supertracks for sweep-line.
  // Excludes in-progress supertracks. Recalculates only when candidates/trackBoxes change.
  const sortedCandidateTracks = useMemo(() => {
    if (!mergeCandidateSts || !trackBoxes) return null;
    const tracks = [];
    for (const { stId } of mergeCandidateSts) {
      if (taskStatuses[stId]?.status === "in progress") continue;
      const st = superTracks?.[stId];
      if (!st?.tracks?.length) continue;
      for (const t of st.tracks) {
        if (t.track_id == null || !trackBoxes[t.track_id]) continue;
        tracks.push({ track_id: t.track_id, stId, frame_start: t.frame_start, frame_end: t.frame_end });
      }
    }
    tracks.sort((a, b) => a.frame_start - b.frame_start);
    return tracks.length > 0 ? tracks : null;
  }, [mergeCandidateSts, trackBoxes, superTracks, taskStatuses]);

  // Hotkey map: stId → number (1-9), assigned once based on distance,
  // only for candidates within 15s of storedEnd.
  const candidateHotkeys = useMemo(() => {
    if (!mergeCandidateSts || !fps) return {};
    const windowFrames = Math.round(15 * fps);
    const map = {};
    let num = 1;
    for (const { stId, distance } of mergeCandidateSts) {
      if (distance > windowFrames) break;
      if (taskStatuses[stId]?.status === "in progress") continue;
      map[stId] = num++;
      if (num > 9) break;
    }
    return map;
  }, [mergeCandidateSts, fps, taskStatuses]);

  // Sweep-line for merge candidates (like ShowAll but filtered)
  useEffect(() => {
    if (showAll) return;
    if (!sortedCandidateTracks || !trackBoxes) {
      if (Object.keys(candidates).length > 0) {
        setCandidates({});
        setTrackIds({});
      }
      return;
    }

    // Show ALL tracks from candidate supertracks (not filtered by currentFrame),
    // so the user can see upcoming candidates before reaching their frame range.
    const newCandidates = {};
    const newIds = {};
    for (const t of sortedCandidateTracks) {
      if (!trackBoxes[t.track_id]) continue;
      newCandidates[t.track_id] = trackBoxes[t.track_id];
      const hotkey = candidateHotkeys[t.stId];
      if (hotkey) newIds[t.track_id] = hotkey;
    }
    setCandidates(newCandidates);
    setTrackIds(newIds);

    // Auto-select closest candidate supertrack
    if (mergeCandidateSts) {
      setSelectedSuperTrackId(mergeCandidateSts[0].stId);
    }
  }, [sortedCandidateTracks, trackBoxes, showAll, candidateHotkeys]);

  // Load track boxes for nearest merge candidates that aren't in the current chunk
  useEffect(() => {
    if (!mergeCandidateSts || !trackBoxes || !videoId) return;
    const toFetch = mergeCandidateSts.slice(0, 20);
    for (const { stId } of toFetch) {
      const st = superTracks?.[stId];
      if (!st?.tracks?.length) continue;
      const allLoaded = st.tracks.every(
        (t) => t.track_id == null || trackBoxes[t.track_id]
      );
      if (allLoaded) continue;
      matchApi.getSuperTrackFullByUid(videoId, stId).then((res) => {
        if (res?.tracks) dispatch(mergeTrackBoxesChunk(res.tracks));
      }).catch((e) => console.warn('[mergeCandidates] fetch error:', e));
    }
  }, [mergeCandidateSts, videoId]);

  // Prefetch next chunk when approaching boundary of loaded data
  useEffect(() => {
    if (dataLoaded && fps) checkPrefetch(currentFrame, fps);
  }, [currentFrame, dataLoaded, fps]);

  useEffect(() => {
    const notAllowed = isTimeInRange(currentFrame, pauseList);
    setAllowed(!notAllowed);

    if (interpol) {
      setInterpolStart(null);
      setInterpolEnd(null);
    }

    if (
      stopFrame !== null &&
      currentFrame > stopFrame &&
      !videoRef?.current?.paused
    ) {
      videoRef.current?.pause();
      if (videoRef.current)
        videoRef.current.currentTime = frameTime * stopFrame;
      setCurrentFrame(stopFrame);
      setPbRate(1);
      setStopFrame(null);
    }
  }, [currentFrame]);

  useEffect(() => {
    if (videoDuration && frameTime) {
      setTotalFrames(Number(Math.round(videoDuration / frameTime)));
    }
  }, [videoDuration, frameTime]);

  function onTrackClicked(trackIndex) {
    const trackId = Object.keys(trackIds).find(
      (trackId) => Number(trackIds[trackId]) === Number(trackIndex)
    );
    setColors({ [trackId]: "yellow" });
    let superTrackId;
    for (let id in superTracks) {
      const superTrack = superTracks[id];
      if (!superTrack || !superTrack.tracks || !Array.isArray(superTrack.tracks)) {
        continue; // Skip empty or invalid supertracks
      }
      const exists = superTrack.tracks.find(
        (track) => Number(track.track_id) === Number(trackId)
      );
      if (exists) {
        superTrackId = id;
        break;
      }
    }
    setSelectedSuperTrackId(superTrackId);
  }

  const keyDownHandler = (event) => {
    if (/^[1-9]$/.test(event.key) && !showAll) {
      onTrackClicked(event.key);
    } else if (event.ctrlKey && event.key === "s") {
      event.preventDefault();
      if (!storingRef.current) store();
    } else if (event.key === "w") {
      event.preventDefault();
      handleNextMissedStep();
    } else if (event.key === "Enter" || event.key === "r") {
      if (selectedSuperTrackId === null) return;
      onMerge();
      setSelectedSuperTrackId(null);
    } else if (event.key === "q") {
      handleNextMissed();
    } else if (event.key === "a") {
      setInterpol(!interpol);
    }
  };

  // #region Navigation handlers
  const handleNextMissed = () => {
    const videoElement = videoRef.current;
    if (!boxes || !totalFrames || !videoElement) return;
    for (let i = currentFrame + 1; i < totalFrames; i++) {
      if (!boxes[i]) {
        videoElement.currentTime += frameTime * (i - currentFrame);
        break;
      }
    }
  };

  const findStopFrame = (currentFrame, allowNavigation = false) => {
    const videoElement = videoRef.current;
    if (!boxes || !totalFrames || !videoElement) return;

    let count = 0;
    let lastFrame = null;
    for (let i = currentFrame + 1; i < totalFrames; i++) {
      if (!boxes[i]) {
        count++;
        if (count >= step) {
          if (lastFrame !== null && lastFrame - step > currentFrame) {
            const targetFrame = lastFrame - step;
            setStopFrame(Math.min(targetFrame + step * 2, totalFrames));
          } else {
            setStopFrame(i);
          }
          setPbRate(3);
          videoRef.current?.play();
          break;
        }
      } else {
        count = 0;
        lastFrame = i;
      }
    }
  };

  const handleNextMissedStep = () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    findStopFrame(currentFrame, false);
  };
  // #endregion Navigation handlers

  const onStepChange = (e) => {
    setStep(Number(e.target.value));
  };
  const onFrameInputChange = (e) => {
    const value = Number(e.target.value);
    if (value >= 0 && value <= totalFrames) {
      handleSeek(value, fps);
      setCurrentFrame(value);
      if (videoRef.current) videoRef.current.currentTime = value * frameTime;
    }
  };

  //TODO: review
  const updateCoords = (coords, interpolOn = false) => {
    let keyFrames;
    if (storingRef.current === true) {
      changedKeyFramesRef.current = [
        ...changedKeyFramesRef.current,
        currentFrame,
      ];
    } else {
      keyFrames = [
        ...changedKeyFrames.filter((frame) => frame !== currentFrame),
        currentFrame,
      ];
      dispatch(setChangedKeyFrames(keyFrames));
    }
    let newCoords = coords;
    if (imageY) {
      newCoords = coords.map((val, i) =>
        [1, 3].includes(i) ? val - imageY : val
      );
    }
    let clonedNewBoxes;
    if (storingRef.current === true) {
      clonedNewBoxes = { ...newBoxesRef.current };
      clonedNewBoxes[currentFrame] = newCoords;
      newBoxesRef.current = { ...clonedNewBoxes };
      dispatch(setBoxes({ ...boxes, ...newBoxesRef.current }));
    } else {
        clonedNewBoxes = { ...newBoxes };
      clonedNewBoxes[currentFrame] = newCoords;
      dispatch(setNewBoxes({ ...clonedNewBoxes }));
    }
    if (interpol && interpolOn) {
      // Save changed frame numbers to use them on overwrite mode.
      if (!changedFrames.includes(currentFrame)) {
        const sorted = [...changedFrames, currentFrame].sort((a, b) => a - b);
        setChangedFrames([...sorted]);
      }
      if (interpolStart !== null || interpolEnd !== null) {
        // interpolStart !== null means the frame hasn't changed after the last interpolation so reapply interpolation on the same frame.
        interpolate(
          newCoords,
          interpolStart,
          currentFrame,
          interpolEnd,
          clonedNewBoxes,
          (newBoxes) => callStore(newBoxes, keyFrames)
        );
      } else {
        // if it's the first time of editing(changedFrames.length > 0) while turning on the overwrite mode
        // we apply the normal mode
        if (overwriteMode && changedFrames.length > 0) {
          let start = null,
            end = null;
          for (let i = 0; i < changedFrames.length; i++) {
            if (changedFrames[i] < currentFrame) {
              start = changedFrames[i];
            } else if (changedFrames[i] > currentFrame) {
              end = changedFrames[i];
              break;
            }
          }
          if (start !== null || end !== null) {
            setInterpolStart(start);
            setInterpolEnd(end);
            interpolate(
              newCoords,
              start,
              currentFrame,
              end,
              clonedNewBoxes,
              (newBoxes) => callStore(newBoxes, keyFrames)
            );
          }
        } else {
          for (let i = currentFrame - 1; i >= frameStart ?? 0; i--) {
            if (isTimeInRange(i, pauseList)) {
              break;
            }
            if (boxes[i] && currentFrame - i <= Math.max(step, 200)) {
              setInterpolStart(i);
              interpolate(
                newCoords,
                i,
                currentFrame,
                null,
                clonedNewBoxes,
                (newBoxes) => callStore(newBoxes, keyFrames)
              );
              break;
            }
          }
        }
      }
    }
  };

  const callStore = (newBoxes, keyFrames) => {
    if (lastUpdatedFrame !== currentFrame) {
      // Store changes at every 20 frames.
      if (changedFrameCnt === 100) {
        store(newBoxes, keyFrames);
      }
      setChangedFrameCnt((prev) => prev + 1);

      setLastUpdatedFrame(currentFrame);
    }
  };

  /**
   * Applies the interpolation from start to target and target to end frame.
   * @param {Array<Number>} newCoords Coords on the current frame
   * @param {Number} start Start frame number of the interpolation
   * @param {Number} target Target frame number of the interpolation
   * @param {Number} end End frame number of the interpolation
   * @param {Object} defaultNewBoxes Default newBoxes object to apply the interpolation
   * @returns
   */
  const interpolate = (
    newCoords,
    start = null,
    target,
    end = null,
    defaultNewBoxes,
    callback
  ) => {
    const clonedNewBoxes = defaultNewBoxes || { ...newBoxes };
    const isBall = superTrackType === "ball";
    const half = BALL_DEFAULT_SIZE / 2;

    if (start !== null) {
      const frameDiff = Math.abs(target - start);
      if (frameDiff < 2 || !boxes[start]) return;

      if (isBall) {
        const startCx = (boxes[start][0] + boxes[start][2]) / 2;
        const startCy = (boxes[start][1] + boxes[start][3]) / 2;
        const endCx = (newCoords[0] + newCoords[2]) / 2;
        const endCy = (newCoords[1] + newCoords[3]) / 2;
        const dxPerFrame = (endCx - startCx) / frameDiff;
        const dyPerFrame = (endCy - startCy) / frameDiff;
        for (let i = start + 1; i < target; i++) {
          const cx = Math.round(startCx + dxPerFrame * (i - start));
          const cy = Math.round(startCy + dyPerFrame * (i - start));
          clonedNewBoxes[i] = [cx - half, cy - half, cx + half, cy + half];
        }
      } else {
        let coordsDiff = [];
        for (let i = 0; i < 4; i++) {
          coordsDiff[i] = (newCoords[i] - boxes[start][i]) / frameDiff;
        }
        for (let i = start + 1; i < target; i++) {
          clonedNewBoxes[i] = [];
          for (let j = 0; j < 4; j++) {
            clonedNewBoxes[i][j] = Math.round(
              boxes[start][j] + coordsDiff[j] * (i - start)
            );
          }
        }
      }
    }
    if (end !== null) {
      const frameDiff = Math.abs(end - target);
      if (frameDiff < 2 || !boxes[end]) return;

      if (isBall) {
        const startCx = (newCoords[0] + newCoords[2]) / 2;
        const startCy = (newCoords[1] + newCoords[3]) / 2;
        const endCx = (boxes[end][0] + boxes[end][2]) / 2;
        const endCy = (boxes[end][1] + boxes[end][3]) / 2;
        const dxPerFrame = (endCx - startCx) / frameDiff;
        const dyPerFrame = (endCy - startCy) / frameDiff;
        for (let i = target + 1; i < end; i++) {
          const cx = Math.round(startCx + dxPerFrame * (i - target));
          const cy = Math.round(startCy + dyPerFrame * (i - target));
          clonedNewBoxes[i] = [cx - half, cy - half, cx + half, cy + half];
        }
      } else {
        let coordsDiff = [];
        for (let i = 0; i < 4; i++) {
          coordsDiff[i] = (boxes[end][i] - newCoords[i]) / frameDiff;
        }
        for (let i = target + 1; i < end; i++) {
          clonedNewBoxes[i] = [];
          for (let j = 0; j < 4; j++) {
            clonedNewBoxes[i][j] = Math.round(
              boxes[target][j] + coordsDiff[j] * (i - target)
            );
          }
        }
      }
    }

    if (storingRef.current === true) {
      newBoxesRef.current = { ...clonedNewBoxes };
      dispatch(setBoxes({ ...boxes, ...newBoxesRef.current }));
    } else {
      dispatch(setNewBoxes({ ...clonedNewBoxes }));
    }
    callback(clonedNewBoxes);
  };

  // triggered when the rect is moved or resized
  // we pass down trackResizable prop to VideoPlayer
  const onResizeRect = (trackId, { x, y, width, height }) => {
    if (!allowed) {
      console.log("not allowed");
      return;
    }

    if (tempRectActive) {
      return setTempRectActive(false);
    }

    const newCoords = [
      Math.round(x / ratio),
      Math.round(y / ratio),
      Math.round((x + width) / ratio),
      Math.round((y + height) / ratio),
    ];
    updateCoords(newCoords, true);
  };

  const store = async (
    boxes = null,
    keyFrames = null,
    substitutionId = null
  ) => {
    try {
      setIsStoring(true);
      storingRef.current = true;
      const pauses = pauseList.map(([start, end, type]) => ({
        start,
        end,
        type,
      }));
      const { supertrack, tracks } = await matchApi.updateSuperTrackFrames(
        videoId,
        trackId,
        boxes || newBoxes,
        keyFrames?.length ? keyFrames : changedKeyFrames,
        taskId ? taskId : null,
        [frameStart, frameEnd],
        pauses,
        substitutionId
      );
      dispatch(setChangedKeyFrames([...changedKeyFramesRef.current]));
      if (Object.keys(newBoxesRef.current).length > 0) {
        dispatch(setNewBoxes({ ...newBoxesRef.current }));
        dispatch(appendFrameBoxes({ newBoxes: tracks }));
        dispatch(
          updateSuperTrack({
            superTrackId: trackId,
            tracks: supertrack,
          })
        );
      } else {
        dispatch(appendFrameBoxes({ newBoxes: tracks }));
        dispatch(
          updateSuperTrack({ superTrackId: trackId, tracks: supertrack })
        );
        dispatch(setNewBoxes({}));
      }
      storingRef.current = false;
      newBoxesRef.current = {};
      setChangedFrameCnt(changedKeyFramesRef.current?.length || 0);
      changedKeyFramesRef.current = [];
      toast.success("Storing Success.");
    } catch (err) {
      Sentry.captureException(err); // Report to Sentry
      console.log(err);
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        err.message ||
        "Something went wrong.";
      toast(errorMessage);
    } finally {
      setIsStoring(false);
    }
  };

  const saveAnswer = async (substitutionId = null) => {
    try {
      setIsSaving(true);
      await store(null, null, substitutionId);
      const query = new URLSearchParams(location.search);
      const taskId = query.get("taskId");
      if (!taskId) {
        toast.warning("taskId is not specificed.");
        return;
      }

      await matchApi.postMatchTaskResult({
        matchId: videoId,
        taskId: taskId,
        taskResult: {
          status: true,
          pauses: pauseList.map(([start, end, type]) => ({
            start,
            end,
            type,
          })),
        },
        taskType,
      });
      toast.success("Saved the answer successfully.");
      navigate(LABELER);
    } catch (err) {
      console.log(err);
      toast(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // this is called when drawing new rect
  const updateSelectionRect = () => {
    if (!allowed) {
      console.log("updateSelectionRect: drawing now allowed");
      return;
    }

    const node = newRectRef.current;
    node.setAttrs({
      visible: newRect.current.visible,
      x:
        (Math.min(newRect.current.x1, newRect.current.x2) - stageX) /
        stageScale,
      y:
        (Math.min(newRect.current.y1, newRect.current.y2) - stageY) /
        stageScale,
      width: Math.abs(newRect.current.x1 - newRect.current.x2) / stageScale,
      height: Math.abs(newRect.current.y1 - newRect.current.y2) / stageScale,
      stroke: "red",
      strokeWidth: 1 / stageScale,
      opacity: 0.6,
    });
    node.getLayer().batchDraw();
  };

  // #region Mouse event handlers
  const BALL_DEFAULT_SIZE = 12; // Default ball square size (px)

  const onMouseDownHandler = (e) => {
    // dragging with Ctrl+LMB
    if (e.evt.button === 0 && e.evt.ctrlKey) return;

    // RMB: Right Mouse Button
    if (e.evt.button === 2) {
      // Clicked on the Empty space
      if (!["rect", "text"].includes(e.target?.attrs.name)) {
        // if we are on empty place of the stage we will do nothing
        setMenuVisible(false);
        setSelectedTrackId(null);
        return;
      }
      let { trackId } = e.target.attrs;
      if (trackId === "main") {
        if (currentSuperTrack) {
          currentSuperTrack.tracks.forEach((t) => {
            const { track_id, frame_start, frame_end } = t;
            if (frame_start <= currentFrame && frame_end >= currentFrame)
              trackId = track_id;
          });
        }
      } else {
        setSelectedTrackId(trackId);
      }

      let superTrackId, existingTrack;
      for (let id in superTracks) {
        const superTrack = superTracks[id];
        if (!superTrack || !superTrack.tracks || !Array.isArray(superTrack.tracks)) {
          continue; // Skip empty or invalid supertracks
        }
        existingTrack = superTrack.tracks.find(
          (track) => Number(track.track_id) === Number(trackId)
        );
        if (existingTrack) {
          superTrackId = id;
          setSelectedTrack(existingTrack);
          break;
        }
      }

      if (existingTrack === undefined) {
        setSelectedTrack(undefined);
      }

      // TODO: if we press enter, it will merge this supertrack. We need to differentiate RMB & Shortkey merging.
      setSelectedSuperTrackId(superTrackId);

      const stage = e.target.getStage();
      // show menu
      setMenuVisible(true);
      menuRef.current.style.top = stage.getPointerPosition().y + 4 + "px";
      menuRef.current.style.left = stage.getPointerPosition().x + 16 + "px";
    }

    if (!allowed) {
      console.log("onMouseDownHandler: drawing now allowed");
      return;
    }

    if (e.target.attrs.name === "tempRect") return;

    let drawMode = false;
    const clickedOnTrack =
      e.target.attrs.name === "rect" ||
      e.target.attrs.name === "text" ||
      (e.target.attrs.name?.includes && e.target.attrs.name.includes("_anchor"));

    if (clickedOnTrack && superTrackType !== "ball") {
      // switch to selection mode (not for ball — ball always places)
      setIsDrawing(false);
    } else {
      drawMode = true;
      // switch to draw mode
      if (e.evt.ctrlKey) return; // don't draw if we are in merge mode
      setIsDrawing(true);
    }

    // don't draw if it's element or transformer
    const isElement = e.target.findAncestor(".elements-container");
    const isTransformer = e.target.findAncestor("Transformer");
    if (isElement || isTransformer) {
      return;
    }

    // start drawing new rect
    const pos = e.target.getStage().getPointerPosition();
    newRect.current.visible = true;
    newRect.current.x1 = pos.x;
    newRect.current.y1 = pos.y;
    newRect.current.x2 = pos.x;
    newRect.current.y2 = pos.y;
    updateSelectionRect();
  };

  const onMouseMoveHandler = (e) => {
    if (e.evt.button === 2) return;
    if (!allowed) return;

    if (!newRect.current.visible || !isDrawing) {
      return;
    }

    const pos = e.target.getStage().getPointerPosition();
    newRect.current.x2 = pos.x;
    newRect.current.y2 = pos.y;
    updateSelectionRect();
  };

  const onMouseUpHandler = (e) => {
    if (e.evt.button === 2) return;

    if (!allowed) return;

    if (e.target.attrs.name === "tempRect") {
      const { x, y, width, height } = e.target.attrs;
      const newCoords = [
        Math.round(x / ratio),
        Math.round(y / ratio),
        Math.round((x + width) / ratio),
        Math.round((y + height) / ratio),
      ];
      updateCoords(newCoords, true);
      setTempRectActive(true);
      if (autoNext) {
        handleNextMissedStep();
      }
      return;
    }

    if (!isDrawing) {
      return;
    }
    newRect.current.visible = false;
    setIsDrawing(false);
    const { x1, x2, y1, y2 } = newRect.current;
    const moved = x1 !== x2 || y1 !== y2;
    if (!moved) {
      // Ball: single click without drag → place default-size square
      if (superTrackType === "ball") {
        const half = (BALL_DEFAULT_SIZE / 2) * ratio * stageScale;
        newRect.current.x1 = x1 - half;
        newRect.current.y1 = y1 - half;
        newRect.current.x2 = x1 + half;
        newRect.current.y2 = y1 + half;
        newRect.current.visible = true;
        updateSelectionRect();
        const node = newRectRef.current;
        const newCoords = [
          Math.round(node.attrs.x / ratio),
          Math.round(node.attrs.y / ratio),
          Math.round((node.attrs.x + node.attrs.width) / ratio),
          Math.round((node.attrs.y + node.attrs.height) / ratio),
        ];
        updateCoords(newCoords, true);
        newRect.current.visible = false;
        updateSelectionRect();
        if (autoNext) handleNextMissedStep();
        return;
      }
      updateSelectionRect();
      return;
    }
    const { x, y, width, height } = newRectRef.current.attrs;
    const newCoords = [
      Math.round(x / ratio),
      Math.round(y / ratio),
      Math.round((x + width) / ratio),
      Math.round((y + height) / ratio),
    ];
    updateCoords(newCoords, true);
    updateSelectionRect();

    if (autoNext) {
      handleNextMissedStep();
    }
  };
  // #endregion Mouse event handlers

  const onJerseyUpdate = useCallback(
    async (e) => {
      const jersey = e.target.value || null;
      await matchApi.postSuperTrackJersey({
        matchId: videoId,
        superTracksId: trackId,
        jersey,
      });
    },
    [videoId, trackId]
  );

  const onJerseyChange = useCallback((e) => {
    setJersey(e.target.value);
  }, []);

  const onFrameChange = useCallback((frameNumber) => {
    setCurrentFrame(frameNumber);
  }, []);

  const onStageScaleChange = useCallback((newStageScale) => {
    setStageScale(newStageScale);
  }, []);

  const onStagePosChange = useCallback((newStageX, newStageY) => {
    setStageX(newStageX);
    setStageY(newStageY);
  }, []);

  const onRatioChange = useCallback((newRatio) => {
    setRatio(newRatio);
  }, []);

  const onVideoSizeChange = useCallback((width, height) => {
    setVideoWidth(width);
    setVideoHeight(height);
  }, []);

  const onVideoDomSizeChange = useCallback((width, height) => {
    setVideoDomWidth(width);
    setVideoDomHeight(height);
  }, []);

  const onSetInitialY = useCallback((y) => {
    setImageY(y);
  }, []);

  const onProgressClick = useCallback(
    (e) => {
      const rect = percentRef.current?.getBoundingClientRect();

      const pos = (e.pageX - rect.left) / percentRef.current?.offsetWidth;
      let targetTime;
      if (frameStart !== null && frameEnd !== null) {
        targetTime = (frameStart + pos * (frameEnd - frameStart)) * (1 / fps);
      } else {
        targetTime = pos * videoDuration;
      }
      const targetFrame = Math.round(targetTime * fps);
      handleSeek(targetFrame, fps);
      videoRef.current.currentTime = targetTime;
    },
    [videoDuration, frameTime, frameStart, frameEnd, fps, handleSeek]
  );

  const onVideoLoadHandler = () => {
    setVideoDuration(videoRef.current.duration);

    // TODO: require refoctoring
    const parts = getFormattedDuration(videoRef.current.duration).split(":");
    const formattedTime = parts[0] + "." + parts[1];

    dispatch(setSuperTrackGlobalVideoDuration(+formattedTime));
    dispatch(setFrameCount(Math.round(videoRef.current.duration / (1 / fps))));
    dispatch(setSuperTrackStartTime(0));
    dispatch(setSuperTrackEndTime(+formattedTime));
  };

  const onTimeUpdateHandler = () => {
    const frameCount = Math.round(videoRef.current.duration / (1 / fps));
    const superTrackStartTime = 0;
    let position =
      Math.round(
        (videoRef.current.currentTime - superTrackStartTime * 60) / (1 / fps)
      ) * 0.5;

    if (position >= frameCount) {
      position = frameCount * 0.5 - superTrackStartTime * 60;
    }
    if (posRef.current) posRef.current.style.left = `${position - 5}px`;

    // Auto-scroll track bar to keep indicator visible
    if (trackBarRef.current) {
      const container = trackBarRef.current;
      const containerWidth = container.clientWidth;
      const scrollLeft = container.scrollLeft;
      // Keep indicator roughly centered in view
      if (position < scrollLeft || position > scrollLeft + containerWidth - 50) {
        container.scrollLeft = position - containerWidth / 2;
      }
    }
  };

  const onMerge = useCallback(async () => {
    setMenuVisible(false);

    if (!selectedSuperTrackId) {
      alert(
        "Please select new super track. And make sure to click rectangle not track id."
      );
      return;
    }

    if (trackId === selectedSuperTrackId) {
      alert(
        "You've selected the target super track. Please select another one."
      );
      return;
    }

    const targetStatus = taskStatuses[selectedSuperTrackId]?.status;
    if (targetStatus === "in progress") {
      toast.warning(`Supertrack #${selectedSuperTrackId} is being worked on by ${taskStatuses[selectedSuperTrackId]?.resolver_name || "another labeler"}`);
      return;
    }

    if (
      hasIntersection({
        targetId: trackId,
        superTrackId: selectedSuperTrackId,
      })
    ) {
      setShowConfirmModal(true);
      return;
    }

    const mergedSuperTrack = superTracks[selectedSuperTrackId];
    const mergeEndFrame = mergedSuperTrack?.tracks?.reduce(
      (max, t) => Math.max(max, t.frame_end ?? 0),
      0
    ) ?? 0;

    merge(videoId, trackId, selectedSuperTrackId);

    if (mergeEndFrame > 0 && videoRef.current) {
      setStopFrame(mergeEndFrame + 30);
      setPbRate(3);
      videoRef.current.play();
    }
  }, [trackId, selectedSuperTrackId, videoId, superTracks, candidates, frameTime]);

  const onCut = async (mode) => {
    const targetStId = selectedSuperTrackId || trackId;
    const targetStatus = taskStatuses[targetStId]?.status;
    if (targetStatus === "in progress" && String(targetStId) !== String(trackId)) {
      toast.warning(`Supertrack #${targetStId} is being worked on by ${taskStatuses[targetStId]?.resolver_name || "another labeler"}`);
      return;
    }
    if (
      [CUT_MODES.CUT, CUT_MODES.CUT_OFF_RIGHT, CUT_MODES.CUT_OFF_LEFT].includes(
        mode
      )
    ) {
      cut(
        videoId,
        targetStId,
        selectedTrack?.track_id,
        currentFrame,
        mode
      );
    }
  };

  useEffect(() => {
    async function cutSuperTrackFunc() {
      if (isCutting.current) {
        // Create a super track
        const newSuperTrack = await matchApi.createSuperTrack(videoId);
        if (!newSuperTrack) return;

        // filter tracks which need to be moved to a new super track
        // TODO: filter tracks correctly
        const selectedSuperTrack = superTracks[selectedSuperTrackId];
        if (!selectedSuperTrack || !selectedSuperTrack.tracks || !Array.isArray(selectedSuperTrack.tracks)) {
          return; // Skip if supertrack is empty or invalid
        }
        const filteredTracks = selectedSuperTrack.tracks.filter(
          (t, index) => {
            return t.frame_start > selectedTrack.frame_start;
          }
        );
        const filteredTrackIds = filteredTracks.map((t) => t.track_id);
        // Move tracks
        replace(
          videoId,
          selectedSuperTrackId,
          newSuperTrack.super_track_uid,
          filteredTrackIds
        );
        // setIsCutting(false);
        isCutting.current = false;
        dispatch(
          cutSuperTrack({
            superTrackId: selectedSuperTrackId,
            newSuperTrack,
            trackIds: filteredTrackIds,
          })
        );
      }
    }
    cutSuperTrackFunc();
  }, [superTracks, selectedTrack, selectedSuperTrackId]);

  const onToggleShowAll = () => {
    setShowAll(!showAll);
  };

  const onTrackBarClick = useCallback(
    (e) => {
      const container = e.currentTarget;
      const rect = container.getBoundingClientRect();
      const scrollLeft = container.scrollLeft;
      const clickX = e.clientX - rect.left + scrollLeft;
      const totalWidth = container.scrollWidth;
      if (totalWidth <= 0) return;
      const ratio = clickX / totalWidth;
      const start = frameStart ?? 0;
      const end = frameEnd || totalFrames;
      const frame = Math.round(start + ratio * (end - start));
      if (videoRef.current) {
        videoRef.current.currentTime = frame * (1 / fps);
      }
    },
    [frameStart, frameEnd, totalFrames, fps]
  );

  useEffect(() => {
    realtimeSync.current = realTimeSync;
  }, [realTimeSync]);

  useEffect(() => {
    if (isCutActive) {
      videoRef.current?.pause();
    }
  }, [isCutActive]);

  useEffect(() => {
    if (isCutActive) {
      videoRef.current.currentTime = cutFrame * (1 / fps);
    }
  }, [cutFrame, fps, isCutActive]);

  useEffect(() => {
    if (!superTracks) return;
    const currentSt = superTracks[trackId];

    // Skip if current ST unchanged and newBoxes unchanged (external event)
    const stChanged = currentSt !== prevProgressStRef.current;
    const nbChanged = newBoxes !== prevProgressBoxesRef.current;
    prevProgressStRef.current = currentSt;
    prevProgressBoxesRef.current = newBoxes;
    if (!stChanged && !nbChanged) return;

    const tracks = currentSt?.tracks || [];
    const frames = Object.keys(newBoxes);

    const result = [];
    if (frames && frames.length > 0) {
      let start = frames[0];
      let end = frames[0];

      for (let i = 1; i < frames.length; i++) {
        // Check if the current frame is consecutive to the previous one
        if (frames[i] === end + 1) {
          end = frames[i]; // Extend the range
        } else {
          // Push the current range to the result
          result.push({ frame_start: start, frame_end: end });
          start = frames[i]; // Start a new range
          end = frames[i]; // Set the end to the new start
        }
      }

      // Push the last range to the result
      result.push({ frame_start: start, frame_end: end });
    }
    const newProgressTracks = [...tracks, ...result];
    if (!isEqual(progressTracks, newProgressTracks))
      setProgressTracks(newProgressTracks);
  }, [superTracks, newBoxes]);

  const handleGamePause = (type) => {
    if (type === "ball_out") {
      if (gamePausedOut) {
        setPauseList([
          ...pauseList,
          [
            Math.round(gamePausedOut / frameTime),
            Math.round(videoRef.current.currentTime / frameTime),
            type,
          ],
        ]);
      }

      setGamePausedOut(gamePausedOut ? null : videoRef.current.currentTime);
    } else if (type === "inactive_ball") {
      if (gamePausedInactive) {
        setPauseList([
          ...pauseList,
          [
            Math.round(gamePausedInactive / frameTime),
            Math.round(videoRef.current.currentTime / frameTime),
            type,
          ],
        ]);
      }

      setGamePausedInactive(
        gamePausedInactive ? null : videoRef.current.currentTime
      );
    }
  };

  useEffect(() => {
    if (taskType === "ball_supertrack") {
      setRealTimeSync(false);
    }
  }, [taskType]);

  useEffect(() => {
    if (currentSuperTrack?.jersey) {
      setJersey(currentSuperTrack.jersey);
    }
  }, [currentSuperTrack]);

  return (
    <>
      {dataLoaded ? (
        <div>
          <Row className="tw-justify-center tw-pt-2">
            <Col
              xs={11}
              className="tw-flex tw-items-center tw-flex-wrap tw-gap-2"
            >
              <span className="tw-my-1">
                Step:{" "}
                <input
                  className="super-track-markup-toolbar__step"
                  type="text"
                  pattern="[0-9]*"
                  value={step}
                  onChange={onStepChange}
                />
              </span>
              <span className="tw-my-1">
                Frame:{" "}
                <input
                  className="super-track-markup-video__frame-input mx-1"
                  type="text"
                  pattern="[0-9]*"
                  value={currentFrame}
                  onChange={onFrameInputChange}
                />
              </span>
              <span>Gaps: {gapCounts}</span>
              {superTrackType === "ball" && frameStart != null && frameEnd != null && fps > 0 && (
                <span className="tw-font-mono tw-text-sm">
                  Range: {Math.floor(frameStart / fps / 60)}:{String(Math.floor((frameStart / fps) % 60)).padStart(2, '0')}
                  -{Math.floor(frameEnd / fps / 60)}:{String(Math.floor((frameEnd / fps) % 60)).padStart(2, '0')}
                </span>
              )}
              <span>
                Jersey:{" "}
                <Form.Control
                  type="text"
                  placeholder="#"
                  size="sm"
                  className="tw-inline-block tw-w-16"
                  value={jersey ?? ""}
                  onChange={onJerseyChange}
                  onBlur={onJerseyUpdate}
                />
              </span>
              <Switch
                label="Interpol:"
                checked={interpol}
                reverse={true}
                onChange={setInterpol}
              />
              <Switch
                label="Overwrite:"
                checked={overwriteMode}
                disabled={!interpol}
                reverse={true}
                onChange={setOverwriteMode}
              />
              <Switch
                label="Zoom:"
                checked={autoZoom}
                reverse={true}
                onChange={setAutoZoom}
              />
              <Switch
                label="Sync:"
                checked={realTimeSync}
                reverse={true}
                onChange={setRealTimeSync}
              />
              <Switch
                label="Auto:"
                checked={autoNext}
                reverse={true}
                onChange={setAutoNext}
              />
              <Form.Check
                className={`switch ${showAll ? "on" : "off"}`}
                reverse
                inline
                type="switch"
                label="All:"
                onChange={onToggleShowAll}
              />
              <Switch
                label="Bar:"
                checked={showTrackBar}
                reverse={true}
                onChange={setShowTrackBar}
              />
              {(taskType === "ball_supertrack" || allowPauseEditing) && (
                <>
                  <MyButton
                    className={cn(
                      "tw-py-0",
                      gamePausedOut ? "tw-animate-bg-transition" : "",
                      gamePausedInactive ? "tw-bg-gray-400" : ""
                    )}
                    size="medium"
                    shape="rect"
                    disabled={gamePausedInactive}
                    icon={
                      <FontAwesomeIcon
                        icon="fa-solid fa-stopwatch"
                        beat={!!gamePausedOut}
                      />
                    }
                    onClick={() => handleGamePause("ball_out")}
                    title="Ball Out (T)"
                  >
                    Out
                  </MyButton>
                  <MyButton
                    className={cn(
                      "tw-py-0",
                      gamePausedInactive ? "tw-animate-bg-transition" : "",
                      gamePausedOut ? "tw-bg-gray-400" : ""
                    )}
                    size="medium"
                    shape="rect"
                    disabled={gamePausedOut}
                    icon={
                      <FontAwesomeIcon
                        icon="fa-solid fa-stopwatch"
                        beat={!!gamePausedInactive}
                      />
                    }
                    onClick={() => handleGamePause("inactive_ball")}
                    title="Inactive Ball (G)"
                  >
                    Inactive
                  </MyButton>
                </>
              )}
              <div className="tw-ml-auto tw-flex tw-items-center tw-gap-1">
                {consistencyStatus && (
                  <div style={{ position: 'relative' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '2px 8px',
                        borderRadius: 6,
                        backgroundColor: consistencyStatus === 'green' ? '#dcfce7' : '#fee2e2',
                        border: `1px solid ${consistencyStatus === 'green' ? '#22c55e' : '#ef4444'}`,
                        cursor: consistencyDiffs.length > 0 ? 'pointer' : 'default',
                        userSelect: 'none',
                        fontSize: 12,
                      }}
                      onClick={() => {
                        if (consistencyDiffs.length > 0) {
                          setShowConsistencyDiffs(prev => !prev);
                        }
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: consistencyStatus === 'green' ? '#22c55e' : '#ef4444',
                        }}
                      />
                      <span style={{ fontWeight: 500, color: consistencyStatus === 'green' ? '#166534' : '#991b1b' }}>
                        {consistencyStatus === 'green' ? 'Synced' : `${consistencyDiffs.length} diff`}
                      </span>
                    </div>
                    {showConsistencyDiffs && consistencyDiffs.length > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: 4,
                          width: 380,
                          maxHeight: 300,
                          overflowY: 'auto',
                          backgroundColor: '#fff',
                          border: '1px solid #ef4444',
                          borderRadius: 8,
                          padding: 10,
                          fontSize: 12,
                          fontFamily: 'monospace',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          zIndex: 1000,
                        }}
                      >
                        {consistencyDiffs.map((d, i) => (
                          <div key={i}>
                            {d.only_local?.length > 0 && (
                              <div style={{ marginBottom: 4 }}>
                                <span style={{ color: '#991b1b', fontWeight: 600 }}>Local only:</span>{' '}
                                {d.only_local.join(', ')}
                              </div>
                            )}
                            {d.only_remote?.length > 0 && (
                              <div style={{ marginBottom: 4 }}>
                                <span style={{ color: '#991b1b', fontWeight: 600 }}>Remote only:</span>{' '}
                                {d.only_remote.join(', ')}
                              </div>
                            )}
                            {d.changed?.length > 0 && (
                              <div>
                                <span style={{ color: '#991b1b', fontWeight: 600 }}>Changed:</span>{' '}
                                {d.changed.join(', ')}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <Button
                  variant="success"
                  size="sm"
                  disabled={isStoring}
                  onClick={() => store()}
                >
                  {isStoring ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />{" "}
                      Storing
                    </>
                  ) : (
                    "Store"
                  )}
                </Button>
                {taskId ? (
                  <Button
                    variant="success"
                    size="sm"
                    disabled={isSaving}
                    onClick={() => setShowSaveConfirm(true)}
                  >
                    {isSaving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />{" "}
                        Saving
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                ) : null}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={isStoring}
                  onClick={async () => {
                    await store();
                    navigate(DASHBOARD);
                  }}
                  title="Store & go to dashboard"
                >
                  <FontAwesomeIcon icon="fa-solid fa-house" />
                </Button>
              </div>
            </Col>
          </Row>
          <Row className="tw-justify-center tw-pt-1">
            <Col className="super-track-markup-progress" xs={11}>
              <div className="tw-relative triangle-caret-container">
                {totalFrames ? (
                  <>
                    <div
                      className="triangle-caret"
                      style={{
                        left:
                          frameStart !== null && frameEnd !== null
                            ? (videoDomWidth * (currentFrame - frameStart)) /
                            (frameEnd - frameStart)
                            : (currentFrame * videoDomWidth) / totalFrames,
                      }}
                    ></div>
                    {(taskType === "ball_supertrack" || allowPauseEditing) &&
                      pauseList.map((pause, index) => (
                        <div
                          key={index}
                          className="tw-absolute -tw-translate-x-1/2 -tw-translate-y-3 tw-text-[10px]"
                          style={{
                            left:
                              frameStart !== null && frameEnd !== null
                                ? (videoDomWidth *
                                  (Math.abs(pause[1] + pause[0]) / 2 -
                                    frameStart)) /
                                (frameEnd - frameStart)
                                : ((Math.abs(pause[1] + pause[0]) / 2 -
                                  frameStart) *
                                  videoDomWidth) /
                                totalFrames,
                          }}
                        >
                          <HourGlassButton
                            onClick={() => {
                              setPauseList([
                                ...pauseList.filter((_, i) => i !== index),
                              ]);
                            }}
                          />
                        </div>
                      ))}
                  </>
                ) : null}
              </div>
              <ProgressBar
                videoDomWidth={videoDomWidth}
                progressTracks={progressTracks}
                pausedList={pauseList}
                frameStart={frameStart ?? 0}
                frameEnd={frameEnd || totalFrames}
                percentRef={percentRef}
                onProgressClick={onProgressClick}
              />
              {showTrackBar && superTracks?.[trackId] && (
                <div
                  ref={trackBarRef}
                  className="super-track__panel-track-container"
                  style={{ height: 40, overflowX: "auto", overflowY: "hidden", cursor: "pointer" }}
                  onClick={onTrackBarClick}
                >
                  <div className="super-track__indicator" ref={posRef} />
                  <div className="inner-container" style={{ height: "auto", overflow: "hidden" }}>
                    <SuperTrackMarkupSuperTrack
                      superTrack={{ ...superTracks[trackId], id: trackId }}
                    />
                  </div>
                </div>
              )}
            </Col>
          </Row>
          <Row className="tw-relative tw-justify-center">
            <Col xs={11} className="tw-relative">
              <VideoPlayer
                ref={videoRef}
                src={videoPath}
                fps={fps}
                step={step}
                frameStep={frameStep}
                videoId={videoId}
                startTime={frameStart !== null ? frameStart * (1 / fps) : null}
                endTime={frameEnd !== null ? frameEnd * (1 / fps) : null}
                tracks={memoizedBoxes}
                colors={{
                  ...colors,
                  main: "yellow",
                }}
                ids={trackIds}
                autoZoom={autoZoom}
                focusedTrackId="main"
                idVisible={taskType === "ball_supertrack" ? false : true}
                trackResizable={allowed && superTrackType !== "ball"}
                showLastTrack={"main"} // trackId of main track which needs to be visible whole time.
                tempRectColor="red"
                zoomButtonsVisible={true}
                pbRate={pbRate}
                onFrameChange={onFrameChange}
                keyDownHandler={keyDownHandler}
                onMouseDownHandler={onMouseDownHandler}
                onMouseMoveHandler={onMouseMoveHandler}
                onMouseUpHandler={onMouseUpHandler}
                onStageScaleChange={onStageScaleChange}
                onStagePosChange={onStagePosChange}
                onRatioChange={onRatioChange}
                onVideoDomSizeChange={onVideoDomSizeChange}
                onVideoSizeChange={onVideoSizeChange}
                onVideoLoadHandler={onVideoLoadHandler}
                onTimeUpdateHandler={onTimeUpdateHandler}
                onResizeTrack={onResizeRect}
                onTrackRightClick={(tid) => {
                  if (!superTracks) return;
                  for (const stId in superTracks) {
                    const st = superTracks[stId];
                    if (!st?.tracks) continue;
                    if (st.tracks.some((t) => String(t.track_id) === String(tid))) {
                      setRightClickedTrackInfo({ stId, trackId: tid });
                      return;
                    }
                  }
                }}
                onSetInitialY={onSetInitialY}
              >
                <Rect ref={newRectRef} name="newMarkup" />
              </VideoPlayer>
              {!allowed && (
                <div className="tw-absolute tw-left-0 tw-top-0 tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center tw-text-red-600 tw-text-9xl tw-pointer-events-none">
                  <FontAwesomeIcon icon="fa-solid fa-stopwatch" beat />
                </div>
              )}
              <ControlledMenu
                ref={menuRef}
                state={menuVisible ? "open" : "closed"}
                direction="right"
                onClose={() => { setMenuVisible(false); setRightClickedTrackInfo(null); }}
                onContextMenu={(e) => e.preventDefault()}
              >
                <MenuItem onClick={() => onMerge()}>Merge</MenuItem>
                {rightClickedTrackInfo && (
                  <>
                    <MenuItem disabled>st #{rightClickedTrackInfo.stId} track #{rightClickedTrackInfo.trackId}</MenuItem>
                    {taskStatuses[rightClickedTrackInfo.stId]?.status === "in progress" &&
                      taskStatuses[rightClickedTrackInfo.stId]?.resolver_name && (
                      <MenuItem disabled>{taskStatuses[rightClickedTrackInfo.stId].resolver_name}</MenuItem>
                    )}
                  </>
                )}
                {!rightClickedTrackInfo && selectedSuperTrackId && selectedSuperTrackId !== trackId && (
                  <MenuItem disabled>ST #{selectedSuperTrackId}</MenuItem>
                )}
                {(selectedSuperTrackId === trackId ||
                  selectedSuperTrackId === undefined) && (
                    <SubMenu label="Cut">
                      <MenuItem onClick={() => onCut(CUT_MODES.CUT)}>
                        Cut
                      </MenuItem>
                      <MenuItem onClick={() => onCut(CUT_MODES.CUT_OFF_RIGHT)}>
                        Cut Off Right
                      </MenuItem>
                      <MenuItem onClick={() => onCut(CUT_MODES.CUT_OFF_LEFT)}>
                        Cut Off Left
                      </MenuItem>
                    </SubMenu>
                  )}
              </ControlledMenu>
            </Col>
          </Row>
          <Modal
            show={showConfirmModal}
            onHide={() => setShowConfirmModal(false)}
          >
            <Modal.Header closeButton>
              <Modal.Title>Merge blocked</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              The two supertracks have an intersection and cannot be merged.
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
          <Modal
            show={showSaveConfirm}
            onHide={() => setShowSaveConfirm(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Confirm Save</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to finish and save?
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowSaveConfirm(false)}>
                No
              </Button>
              <Button variant="success" onClick={() => { setShowSaveConfirm(false); saveAnswer(); }}>
                Yes
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      ) : (
        <Preloader className="tw-m-auto" />
      )}
    </>
  );
}
